import { createContext, useContext } from "react";
import { useAuth } from "./AuthContext";
import {
  LayoutDashboard,
  Users,
  Box,
  Grid3X3,
  Beaker,
  Package,
  Calendar,
  CalendarDays,
  Flame,
  ClipboardCheck,
  Truck,
  BarChart3,
  Warehouse,
  Navigation,
  ShoppingCart,
  Building2,
  FileSpreadsheet,
  Receipt,
  BookOpen,
  DollarSign,
  CreditCard,
  Wallet,
  Settings,
  Database,
  Download,
  Upload,
  Shield,
  Bell,
  Activity,
  History,
  Store,
  Scale,
  Hash,
  Banknote,
  Zap,
  FlaskConical,
  PackageCheck,
} from "lucide-react";

const PermissionsContext = createContext(null);

// All available permissions in the system
export const ALL_PERMISSIONS = {
  // Dashboard
  view_dashboard: "View Dashboard",

  // Masters - Customers
  view_customers: "View Customers",
  add_customers: "Add Customers",
  edit_customers: "Edit Customers",
  delete_customers: "Delete Customers",

  // Marketing - Leads
  view_marketing: "View Marketing",
  add_marketing: "Add Marketing Leads",
  edit_marketing: "Edit Marketing Leads",
  delete_marketing: "Delete Marketing Leads",

  // Masters - Castings
  view_castings: "View Castings",
  add_castings: "Add Castings",
  edit_castings: "Edit Castings",
  delete_castings: "Delete Castings",

  // Masters - Patterns
  view_patterns: "View Patterns",
  add_patterns: "Add Patterns",
  edit_patterns: "Edit Patterns",
  delete_patterns: "Delete Patterns",

  // Masters - Grades
  view_grades: "View Grades",
  add_grades: "Add Grades",
  edit_grades: "Edit Grades",
  delete_grades: "Delete Grades",

  // Masters - Materials
  view_materials: "View Materials",
  add_materials: "Add Materials",
  edit_materials: "Edit Materials",
  delete_materials: "Delete Materials",

  // System - Furnaces
  view_furnaces: "View Furnaces",
  add_furnaces: "Add Furnaces",
  edit_furnaces: "Edit Furnaces",
  delete_furnaces: "Delete Furnaces",

  // Production - Monthly Plan
  view_monthly_plan: "View Monthly Plan",
  add_monthly_plan: "Add Monthly Plan",
  edit_monthly_plan: "Edit Monthly Plan",
  delete_monthly_plan: "Delete Monthly Plan",

  // Production - Daily Plan
  view_daily_plan: "View Daily Plan",
  add_daily_plan: "Add Daily Plan",
  edit_daily_plan: "Edit Daily Plan",
  delete_daily_plan: "Delete Daily Plan",

  // Production - Heats
  view_heats: "View Heats",
  add_heats: "Add Heats",
  edit_heats: "Edit Heats",
  delete_heats: "Delete Heats",
  unlock_heats: "Unlock Heats",

  // Sub Connect - Fettling (weight-only: no add/delete, just view + edit
  // an existing casting's weight — see routes/fettling.js)
  view_fettling: "View Fettling",
  edit_fettling: "Edit Fettling (change casting weight)",

  // Sub Connect - Pattern Num (BPN for Thermocol pattern contract lines)
  view_pattern_num: "View Pattern Num",
  edit_pattern_num: "Edit Pattern Num (assign BPN)",

  // Sub Connect - Ready Cheque (cheques written for vendors, signed or not,
  // linked to their POs, until handed over — see routes/readyCheques.js)
  view_ready_cheques: "View Ready Cheque",
  add_ready_cheques: "Add Ready Cheque",
  edit_ready_cheques: "Edit Ready Cheque (sign / give)",
  delete_ready_cheques: "Delete Ready Cheque",

  // Sub Connect - MSEDCL Reading (daily electricity meter reading; KWH.DIFF /
  // KVAH.DIFF / CAL.PF calculated — see routes/msedclReadings.js)
  view_msedcl_readings: "View MSEDCL Reading",
  add_msedcl_readings: "Add MSEDCL Reading",
  edit_msedcl_readings: "Edit MSEDCL Reading",
  delete_msedcl_readings: "Delete MSEDCL Reading",

  // Sub Connect - Sand Testing Report (fills the connected Docs sheet and saves
  // it as an Exce Report for the supplier — see routes/sandTestingReports.js)
  view_sand_testing_reports: "View Sand Testing Report",
  add_sand_testing_reports: "Add Sand Testing Report",
  edit_sand_testing_reports: "Edit Sand Testing Report (result, note, connect doc)",

  // Sub Connect - Closing Stock (month-end stock in the connected register
  // sheet, booked into Stock as next month's opening — see routes/closingStock.js)
  view_closing_stock: "View Closing Stock",
  add_closing_stock: "Enter / Book Closing Stock",
  edit_closing_stock: "Edit Closing Stock (link materials, connect doc)",
  delete_closing_stock: "Undo Closing Stock booking",

  // Production - Quality
  view_quality: "View Quality",
  add_quality: "Add Quality",
  edit_quality: "Edit Quality",
  delete_quality: "Delete Quality",

  // ExceReport (saved, printable Docs snapshots — Sand Testing Report etc.)
  view_exce_reports: "View Exce Reports",
  add_exce_reports: "Add Exce Reports",
  edit_exce_reports: "Edit Exce Reports",
  delete_exce_reports: "Delete Exce Reports",

  // Sales & Dispatch
  view_sales: "View Sales",
  add_sales: "Add Sales",
  edit_sales: "Edit Sales",
  delete_sales: "Delete Sales",
  copy_mode_sales: "Extend Mode (Tally)",

  view_dispatch: "View Dispatch",
  add_dispatch: "Add Dispatch",
  edit_dispatch: "Edit Dispatch",
  delete_dispatch: "Delete Dispatch",

  view_status: "View Status/Tracking",

  view_stock: "View Stock",
  edit_stock: "Edit Stock",

  // Store
  view_store: "View Store Transactions",
  add_store_request: "Create Store Requests",
  issue_store_materials: "Issue Store Materials",
  return_store_materials: "Return Store Materials",
  close_store_transaction: "Close Store Transactions",
  cancel_store_transaction: "Cancel Store Transactions",
  view_store_history: "View Store History",
  view_store_consumption: "View Store Consumption",
  add_store_consumption: "Add Store Consumption",
  edit_store_consumption: "Edit Store Consumption",
  delete_store_consumption: "Delete Store Consumption",

  // Purchase - Vendors
  view_vendors: "View Vendors",
  add_vendors: "Add Vendors",
  edit_vendors: "Edit Vendors",
  delete_vendors: "Delete Vendors",

  // Purchase - Orders
  view_purchase_orders: "View Purchase Orders",
  add_purchase_orders: "Add Purchase Orders",
  edit_purchase_orders: "Edit Purchase Orders",
  delete_purchase_orders: "Delete Purchase Orders",

  // Purchase - Challans (GRN)
  view_purchase_challans: "View Purchase Challans",
  add_purchase_challans: "Add Purchase Challans",
  edit_purchase_challans: "Edit Purchase Challans",
  delete_purchase_challans: "Delete Purchase Challans",

  // Purchase - Invoices
  view_purchase_invoices: "View Purchase Invoices",
  add_purchase_invoices: "Add Purchase Invoices",
  edit_purchase_invoices: "Edit Purchase Invoices",
  delete_purchase_invoices: "Delete Purchase Invoices",

  // Purchase - Vendor Ledger
  view_vendor_ledger: "View Vendor Ledger",

  // Accounting
  view_chart_of_accounts: "View Chart of Accounts",
  add_accounts: "Add Accounts",
  edit_accounts: "Edit Accounts",
  delete_accounts: "Delete Accounts",

  view_general_ledger: "View General Ledger",
  add_ledger: "Add Ledger",

  view_bank_accounts: "View Bank Accounts",
  add_bank_accounts: "Add Bank Accounts",
  edit_bank_accounts: "Edit Bank Accounts",
  delete_bank_accounts: "Delete Bank Accounts",

  view_receipts: "View Receipts",
  add_receipts: "Add Receipts",
  edit_receipts: "Edit Receipts",
  delete_receipts: "Delete Receipts",

  view_payments: "View Payments",
  add_payments: "Add Payments",
  edit_payments: "Edit Payments",
  delete_payments: "Delete Payments",

  view_journal_entries: "View Journal Entries",
  add_journal_entries: "Add Journal Entries",
  edit_journal_entries: "Edit Journal Entries",
  delete_journal_entries: "Delete Journal Entries",

  view_sales_invoices: "View Sales Invoices",
  add_sales_invoices: "Add Sales Invoices",
  edit_sales_invoices: "Edit Sales Invoices",
  delete_sales_invoices: "Delete Sales Invoices",
  write_accounting: "Write Access to Accounting",

  view_payment_receipts: "View Payment Receipts",
  add_payment_receipts: "Add Payment Receipts",
  edit_payment_receipts: "Edit Payment Receipts",

  view_trial_balance: "View Trial Balance",
  view_customer_ledger: "View Customer Ledger",
  view_profit_loss: "View Profit & Loss",
  view_balance_sheet: "View Balance Sheet",
  view_day_book: "View Day Book",
  view_outstanding: "View Bills Receivable/Payable",
  view_cost_centres: "View Cost Centre Summary",
  view_bank_reconciliation: "View Bank Reconciliation",

  // Reports
  view_reports: "View Reports",
  view_report_master: "View Master Report",
  view_report_heats: "View Heat Report",
  view_report_sales: "View Sales Analysis",
  view_report_costing: "View Costing Report",
  export_reports: "Export Reports",
  edit_costing: "Edit Costing Inputs",

  // System
  view_business_profile: "View Business Profile",
  edit_business_profile: "Edit Business Profile",

  view_users: "View Users",
  add_users: "Add Users",
  edit_users: "Edit Users",
  delete_users: "Delete Users",
  block_users: "Block/Unblock Users",
  hold_users: "Hold/Activate Users",
  manage_permissions: "Manage User Permissions",
  view_user_devices: "View User Devices",
  revoke_user_devices: "Revoke User Devices",

  import_data: "Import Data",
  export_data: "Export Data",

  // Sub Contract / Machining
  view_machining: "View Sub Contract / Machining",
  add_machining: "Add Machining Orders",
  edit_machining: "Edit Machining Orders",
  delete_machining: "Delete Machining Orders",

  // Chemistry Elements
  view_chemistry_elements: "View Chemistry Elements",
  add_chemistry_elements: "Add Chemistry Elements",
  edit_chemistry_elements: "Edit Chemistry Elements",
  delete_chemistry_elements: "Delete Chemistry Elements",

  // Tally Integration
  view_tally_co: "View Tally Integration",
  sync_tally_co: "Sync Tally Data",
  edit_tally_co: "Edit Tally Mappings",

  // Tally Bridge (Accounting → maps Tally customers/ledgers/stock items to ERP records)
  view_tally_bridge: "View Tally Bridge",
  edit_tally_bridge: "Edit Tally Bridge Mappings",

  // Print Templates
  view_print_templates: "View Print Templates",
  add_print_templates: "Add Print Templates",
  edit_print_templates: "Edit Print Templates",
  delete_print_templates: "Delete Print Templates",

  // Approvals
  view_approvals: "View Approvals",
  manage_approvals: "Manage Approval Settings",
  approve_requests: "Approve/Reject Requests",

  // Notifications
  send_notifications: "Send Notifications",
  send_announcement: "Send Announcement Notifications",
  view_notification_history: "View Notification History",

  // Developer
  developer_access: "Developer Dashboard Access",

  // Dashboards
  manage_dashboards: "Manage Custom Dashboards",

  // Activity Log
  view_activity_log: "View Activity Log",

  // AI Assistant
  use_ai_assistant: "Use AI Assistant",

  // Special Pages — B&B (Bath & Beauty Shopify workspace). Deliberately NOT
  // view_-prefixed: empty-map users get every view_* key by role default, and
  // B&B must stay closed unless explicitly granted (backend mirrors this).
  access_bnb: "Access B&B (Bath & Beauty)",
  edit_bnb: "Edit B&B Data & Sheets",

  // Special Pages — BANAMOB module shell (formerly BANA ALERT app). Same
  // reasoning as B&B above: not view_-prefixed, closed by default.
  access_banamob: "Access BANAMOB",
  edit_banamob: "Edit BANAMOB Data",
};

// Predefined role templates with their permissions
export const ROLE_TEMPLATES = [
  {
    name: "Owner",
    color: "purple",
    description: "Full access to all features",
    permissions: Object.keys(ALL_PERMISSIONS).reduce((acc, key) => ({ ...acc, [key]: true }), {})
  },
  {
    name: "Admin",
    color: "blue",
    description: "Full access except user management",
    permissions: {
      view_dashboard: true,
      view_customers: true, add_customers: true, edit_customers: true, delete_customers: true,
      view_marketing: true, add_marketing: true, edit_marketing: true, delete_marketing: true,
      view_castings: true, add_castings: true, edit_castings: true, delete_castings: true,
      view_patterns: true, add_patterns: true, edit_patterns: true, delete_patterns: true,
      view_grades: true, add_grades: true, edit_grades: true, delete_grades: true,
      view_materials: true, add_materials: true, edit_materials: true, delete_materials: true,
      view_furnaces: true, add_furnaces: true, edit_furnaces: true, delete_furnaces: true,
      view_monthly_plan: true, add_monthly_plan: true, edit_monthly_plan: true, delete_monthly_plan: true,
      view_daily_plan: true, add_daily_plan: true, edit_daily_plan: true, delete_daily_plan: true,
      view_heats: true, add_heats: true, edit_heats: true, delete_heats: true, unlock_heats: true,
      view_fettling: true, edit_fettling: true,
      view_pattern_num: true, edit_pattern_num: true,
      view_msedcl_readings: true, add_msedcl_readings: true, edit_msedcl_readings: true, delete_msedcl_readings: true,
      view_sand_testing_reports: true, add_sand_testing_reports: true, edit_sand_testing_reports: true,
      view_closing_stock: true, add_closing_stock: true, edit_closing_stock: true, delete_closing_stock: true,
      view_quality: true, add_quality: true, edit_quality: true, delete_quality: true,
      view_exce_reports: true, add_exce_reports: true, edit_exce_reports: true, delete_exce_reports: true,
      view_sales: true, add_sales: true, edit_sales: true, delete_sales: true,
      view_dispatch: true, add_dispatch: true, edit_dispatch: true, delete_dispatch: true,
      view_status: true, view_stock: true, edit_stock: true,
      view_vendors: true, add_vendors: true, edit_vendors: true, delete_vendors: true,
      view_purchase_orders: true, add_purchase_orders: true, edit_purchase_orders: true, delete_purchase_orders: true,
      view_purchase_challans: true, add_purchase_challans: true, edit_purchase_challans: true, delete_purchase_challans: true,
      view_purchase_invoices: true, add_purchase_invoices: true, edit_purchase_invoices: true, delete_purchase_invoices: true,
      view_vendor_ledger: true,
      view_chart_of_accounts: true, add_accounts: true, edit_accounts: true, delete_accounts: true,
      view_general_ledger: true, add_ledger: true,
      view_bank_accounts: true, add_bank_accounts: true, edit_bank_accounts: true, delete_bank_accounts: true,
      view_receipts: true, add_receipts: true, edit_receipts: true, delete_receipts: true,
      view_payments: true, add_payments: true, edit_payments: true, delete_payments: true,
      view_ready_cheques: true, add_ready_cheques: true, edit_ready_cheques: true, delete_ready_cheques: true,
      view_journal_entries: true, add_journal_entries: true, edit_journal_entries: true, delete_journal_entries: true,
      view_sales_invoices: true, add_sales_invoices: true, edit_sales_invoices: true, delete_sales_invoices: true,
      write_accounting: true,
      view_payment_receipts: true, add_payment_receipts: true, edit_payment_receipts: true,
      view_trial_balance: true, view_customer_ledger: true, view_profit_loss: true, view_balance_sheet: true,
      view_day_book: true, view_outstanding: true, view_cost_centres: true, view_bank_reconciliation: true,
      view_reports: true, export_reports: true, edit_costing: true,
      view_business_profile: true, edit_business_profile: true,
      view_users: true, add_users: true, edit_users: true,
      import_data: true, export_data: true,
      view_print_templates: true, add_print_templates: true, edit_print_templates: true, delete_print_templates: true,
      view_approvals: true, manage_approvals: true, approve_requests: true,
      send_notifications: true, send_announcement: true, view_notification_history: true,
      developer_access: true,
      manage_dashboards: true,
      view_store: true, add_store_request: true, issue_store_materials: true, return_store_materials: true, close_store_transaction: true, cancel_store_transaction: true, view_store_history: true, view_store_consumption: true, add_store_consumption: true, edit_store_consumption: true, delete_store_consumption: true,
      view_machining: true, add_machining: true, edit_machining: true, delete_machining: true,
      view_chemistry_elements: true, add_chemistry_elements: true, edit_chemistry_elements: true, delete_chemistry_elements: true,
      view_tally_co: true, sync_tally_co: true, edit_tally_co: true,
      view_tally_bridge: true, edit_tally_bridge: true,
      view_activity_log: true,
      use_ai_assistant: true,
    }
  },
  {
    name: "Production Manager",
    color: "green",
    description: "Manage production planning and heats",
    permissions: {
      view_dashboard: true,
      view_castings: true, add_castings: true, edit_castings: true,
      view_patterns: true, add_patterns: true, edit_patterns: true,
      view_grades: true,
      view_materials: true, add_materials: true, edit_materials: true,
      view_furnaces: true,
      view_monthly_plan: true, add_monthly_plan: true, edit_monthly_plan: true,
      view_daily_plan: true, add_daily_plan: true, edit_daily_plan: true,
      view_heats: true, add_heats: true, edit_heats: true,
      view_fettling: true, edit_fettling: true,
      view_pattern_num: true, edit_pattern_num: true,
      view_msedcl_readings: true, add_msedcl_readings: true, edit_msedcl_readings: true,
      view_closing_stock: true, add_closing_stock: true,
      view_quality: true, add_quality: true, edit_quality: true,
      view_status: true, view_stock: true,
      view_store: true, add_store_request: true, issue_store_materials: true, return_store_materials: true, close_store_transaction: true, cancel_store_transaction: true, view_store_history: true, view_store_consumption: true, add_store_consumption: true, edit_store_consumption: true,
      view_machining: true, add_machining: true, edit_machining: true,
      view_chemistry_elements: true,
      view_reports: true,
    }
  },
  {
    name: "Quality Controller",
    color: "teal",
    description: "Quality inspection and testing",
    permissions: {
      view_dashboard: true,
      view_castings: true,
      view_patterns: true,
      view_grades: true,
      view_heats: true,
      view_quality: true, add_quality: true, edit_quality: true,
      view_sand_testing_reports: true, add_sand_testing_reports: true,
      view_status: true,
      view_reports: true,
    }
  },
  {
    name: "Sales Manager",
    color: "orange",
    description: "Manage customers, sales, and dispatch",
    permissions: {
      view_dashboard: true,
      view_customers: true, add_customers: true, edit_customers: true,
      view_castings: true,
      view_sales: true, add_sales: true, edit_sales: true,
      view_dispatch: true, add_dispatch: true, edit_dispatch: true,
      view_status: true, view_stock: true,
      view_sales_invoices: true, add_sales_invoices: true, edit_sales_invoices: true,
      view_payment_receipts: true, add_payment_receipts: true,
      view_customer_ledger: true,
      view_reports: true, export_reports: true,
    }
  },
  {
    name: "Accountant",
    color: "indigo",
    description: "Full access to accounting features",
    permissions: {
      view_dashboard: true,
      view_customers: true,
      view_vendors: true,
      view_chart_of_accounts: true, add_accounts: true, edit_accounts: true, delete_accounts: true,
      view_general_ledger: true, add_ledger: true,
      view_bank_accounts: true, add_bank_accounts: true, edit_bank_accounts: true,
      view_receipts: true, add_receipts: true, edit_receipts: true,
      view_payments: true, add_payments: true, edit_payments: true,
      view_ready_cheques: true, add_ready_cheques: true, edit_ready_cheques: true, delete_ready_cheques: true,
      view_journal_entries: true, add_journal_entries: true, edit_journal_entries: true,
      view_sales_invoices: true, add_sales_invoices: true, edit_sales_invoices: true,
      write_accounting: true,
      view_purchase_invoices: true, add_purchase_invoices: true, edit_purchase_invoices: true,
      view_vendor_ledger: true,
      view_payment_receipts: true, add_payment_receipts: true, edit_payment_receipts: true,
      view_trial_balance: true, view_customer_ledger: true, view_profit_loss: true, view_balance_sheet: true,
      view_day_book: true, view_outstanding: true, view_cost_centres: true, view_bank_reconciliation: true,
      view_reports: true, export_reports: true, edit_costing: true,
      view_business_profile: true,
      view_tally_co: true, sync_tally_co: true, edit_tally_co: true,
      view_tally_bridge: true, edit_tally_bridge: true,
      view_approvals: true, approve_requests: true,
    }
  },
  {
    name: "Purchase Manager",
    color: "amber",
    description: "Manage vendors and purchases",
    permissions: {
      view_dashboard: true,
      view_materials: true, add_materials: true, edit_materials: true,
      view_vendors: true, add_vendors: true, edit_vendors: true,
      view_purchase_orders: true, add_purchase_orders: true, edit_purchase_orders: true,
      view_purchase_challans: true, add_purchase_challans: true, edit_purchase_challans: true,
      view_purchase_invoices: true, add_purchase_invoices: true, edit_purchase_invoices: true,
      view_vendor_ledger: true,
      view_stock: true,
      view_store: true, view_store_history: true, view_store_consumption: true,
      view_reports: true,
    }
  },
  {
    name: "Operator",
    color: "gray",
    description: "View-only access to basic features",
    permissions: {
      view_dashboard: true,
      view_castings: true,
      view_patterns: true,
      view_grades: true,
      view_daily_plan: true,
      view_heats: true,
      view_status: true,
      view_store: true, add_store_request: true,
    }
  },
  {
    name: "Developer",
    color: "cyan",
    description: "Full access to all features (same as Owner)",
    permissions: Object.keys(ALL_PERMISSIONS).reduce((acc, key) => ({ ...acc, [key]: true }), {})
  },
];

// Top-level module sections that group the permission categories below, so the
// User Permissions page can be scanned by module (Master, Production, Accounting…)
// instead of as one long flat list. Order here is the display order.
export const PERMISSION_GROUPS = [
  { id: "dashboard", name: "Dashboard" },
  { id: "masters", name: "Master Module" },
  { id: "marketing", name: "Marketing Module" },
  { id: "sales", name: "Sales & Dispatch Module" },
  { id: "purchase", name: "Purchase Module" },
  { id: "subcontract", name: "Sub Contract Module" },
  { id: "store", name: "Store Module" },
  { id: "production", name: "Production Module" },
  { id: "subconnect", name: "Sub Connect Module" },
  { id: "accounting", name: "Accounting Module" },
  { id: "reports", name: "Reports Module" },
  { id: "system", name: "System Module" },
];

// Permission categories for the UI. Each carries a `group` (see PERMISSION_GROUPS)
// so UserPermissionsPage can render them under a module section header.
export const PERMISSION_CATEGORIES = [
  {
    name: "Dashboard",
    group: "dashboard",
    icon: LayoutDashboard,
    permissions: [
      { key: "view_dashboard", label: "View Dashboard" },
    ]
  },
  {
    name: "Customers",
    group: "masters",
    icon: Users,
    permissions: [
      { key: "view_customers", label: "View Customers" },
      { key: "add_customers", label: "Add Customers" },
      { key: "edit_customers", label: "Edit Customers" },
      { key: "delete_customers", label: "Delete Customers" },
    ]
  },
  {
    name: "Marketing",
    group: "marketing",
    icon: Users,
    permissions: [
      { key: "view_marketing", label: "View Marketing" },
      { key: "add_marketing", label: "Add Marketing Leads" },
      { key: "edit_marketing", label: "Edit Marketing Leads" },
      { key: "delete_marketing", label: "Delete Marketing Leads" },
    ]
  },
  {
    name: "Castings",
    group: "masters",
    icon: Box,
    permissions: [
      { key: "view_castings", label: "View Castings" },
      { key: "add_castings", label: "Add Castings" },
      { key: "edit_castings", label: "Edit Castings" },
      { key: "delete_castings", label: "Delete Castings" },
    ]
  },
  {
    name: "Patterns",
    group: "masters",
    icon: Grid3X3,
    permissions: [
      { key: "view_patterns", label: "View Patterns" },
      { key: "add_patterns", label: "Add Patterns" },
      { key: "edit_patterns", label: "Edit Patterns" },
      { key: "delete_patterns", label: "Delete Patterns" },
    ]
  },
  {
    name: "Grades",
    group: "masters",
    icon: Beaker,
    permissions: [
      { key: "view_grades", label: "View Grades" },
      { key: "add_grades", label: "Add Grades" },
      { key: "edit_grades", label: "Edit Grades" },
      { key: "delete_grades", label: "Delete Grades" },
    ]
  },
  {
    name: "Materials",
    group: "masters",
    icon: Package,
    permissions: [
      { key: "view_materials", label: "View Materials" },
      { key: "add_materials", label: "Add Materials" },
      { key: "edit_materials", label: "Edit Materials" },
      { key: "delete_materials", label: "Delete Materials" },
    ]
  },
  {
    name: "Furnaces",
    group: "masters",
    icon: Flame,
    permissions: [
      { key: "view_furnaces", label: "View Furnaces" },
      { key: "add_furnaces", label: "Add Furnaces" },
      { key: "edit_furnaces", label: "Edit Furnaces" },
      { key: "delete_furnaces", label: "Delete Furnaces" },
    ]
  },
  {
    name: "Monthly Plan",
    group: "production",
    icon: Calendar,
    permissions: [
      { key: "view_monthly_plan", label: "View Monthly Plan" },
      { key: "add_monthly_plan", label: "Add Monthly Plan" },
      { key: "edit_monthly_plan", label: "Edit Monthly Plan" },
      { key: "delete_monthly_plan", label: "Delete Monthly Plan" },
    ]
  },
  {
    name: "Daily Plan",
    group: "production",
    icon: CalendarDays,
    permissions: [
      { key: "view_daily_plan", label: "View Daily Plan" },
      { key: "add_daily_plan", label: "Add Daily Plan" },
      { key: "edit_daily_plan", label: "Edit Daily Plan" },
      { key: "delete_daily_plan", label: "Delete Daily Plan" },
    ]
  },
  {
    name: "Heats",
    group: "production",
    icon: Flame,
    permissions: [
      { key: "view_heats", label: "View Heats" },
      { key: "add_heats", label: "Add Heats" },
      { key: "edit_heats", label: "Edit Heats" },
      { key: "delete_heats", label: "Delete Heats" },
      { key: "unlock_heats", label: "Unlock Heats" },
    ]
  },
  {
    name: "Fettling",
    group: "subconnect",
    icon: Scale,
    permissions: [
      { key: "view_fettling", label: "View Fettling" },
      { key: "edit_fettling", label: "Edit Fettling (change casting weight)" },
    ]
  },
  {
    name: "Pattern Num",
    group: "subconnect",
    icon: Hash,
    permissions: [
      { key: "view_pattern_num", label: "View Pattern Num" },
      { key: "edit_pattern_num", label: "Edit Pattern Num (assign BPN)" },
    ]
  },
  {
    name: "Ready Cheque",
    group: "subconnect",
    icon: Banknote,
    permissions: [
      { key: "view_ready_cheques", label: "View Ready Cheque" },
      { key: "add_ready_cheques", label: "Add Ready Cheque" },
      { key: "edit_ready_cheques", label: "Edit Ready Cheque (sign / give)" },
      { key: "delete_ready_cheques", label: "Delete Ready Cheque" },
    ]
  },
  {
    name: "MSEDCL Reading",
    group: "subconnect",
    icon: Zap,
    permissions: [
      { key: "view_msedcl_readings", label: "View MSEDCL Reading" },
      { key: "add_msedcl_readings", label: "Add MSEDCL Reading" },
      { key: "edit_msedcl_readings", label: "Edit MSEDCL Reading" },
      { key: "delete_msedcl_readings", label: "Delete MSEDCL Reading" },
    ]
  },
  {
    name: "Sand Testing Report",
    group: "subconnect",
    icon: FlaskConical,
    permissions: [
      { key: "view_sand_testing_reports", label: "View Sand Testing Report" },
      { key: "add_sand_testing_reports", label: "Add Sand Testing Report" },
      { key: "edit_sand_testing_reports", label: "Edit Sand Testing Report (result, note, connect doc)" },
    ]
  },
  {
    name: "Closing Stock",
    group: "subconnect",
    icon: PackageCheck,
    permissions: [
      { key: "view_closing_stock", label: "View Closing Stock" },
      { key: "add_closing_stock", label: "Enter / Book Closing Stock" },
      { key: "edit_closing_stock", label: "Edit Closing Stock (link materials, connect doc)" },
      { key: "delete_closing_stock", label: "Undo Closing Stock booking" },
    ]
  },
  {
    name: "Quality",
    group: "production",
    icon: ClipboardCheck,
    permissions: [
      { key: "view_quality", label: "View Quality" },
      { key: "add_quality", label: "Add Quality" },
      { key: "edit_quality", label: "Edit Quality" },
      { key: "delete_quality", label: "Delete Quality" },
    ]
  },
  {
    name: "Exce Reports",
    group: "production",
    icon: ClipboardCheck,
    permissions: [
      { key: "view_exce_reports", label: "View Exce Reports" },
      { key: "add_exce_reports", label: "Add Exce Reports" },
      { key: "edit_exce_reports", label: "Edit Exce Reports" },
      { key: "delete_exce_reports", label: "Delete Exce Reports" },
    ]
  },
  {
    name: "Sales",
    group: "sales",
    icon: DollarSign,
    permissions: [
      { key: "view_sales", label: "View Sales" },
      { key: "add_sales", label: "Add Sales" },
      { key: "edit_sales", label: "Edit Sales" },
      { key: "delete_sales", label: "Delete Sales" },
      { key: "copy_mode_sales", label: "Extend Mode (Tally)" },
    ]
  },
  {
    name: "Dispatch",
    group: "sales",
    icon: Truck,
    permissions: [
      { key: "view_dispatch", label: "View Dispatch" },
      { key: "add_dispatch", label: "Add Dispatch" },
      { key: "edit_dispatch", label: "Edit Dispatch" },
      { key: "delete_dispatch", label: "Delete Dispatch" },
    ]
  },
  {
    name: "Status & Stock",
    group: "sales",
    icon: Warehouse,
    permissions: [
      { key: "view_status", label: "View Status/Tracking" },
      { key: "view_stock", label: "View Stock" },
      { key: "edit_stock", label: "Edit Stock" },
    ]
  },
  {
    name: "Store",
    group: "store",
    icon: Package,
    permissions: [
      { key: "view_store", label: "View Store Transactions" },
      { key: "add_store_request", label: "Create Store Requests" },
      { key: "issue_store_materials", label: "Issue Materials" },
      { key: "return_store_materials", label: "Return Materials" },
      { key: "close_store_transaction", label: "Close Transactions" },
      { key: "cancel_store_transaction", label: "Cancel Transactions" },
      { key: "view_store_history", label: "View Store History" },
      { key: "view_store_consumption", label: "View Consumption" },
      { key: "add_store_consumption", label: "Add Consumption" },
      { key: "edit_store_consumption", label: "Edit Consumption" },
      { key: "delete_store_consumption", label: "Delete Consumption" },
    ]
  },
  {
    name: "Vendors",
    group: "masters",
    icon: Building2,
    permissions: [
      { key: "view_vendors", label: "View Vendors" },
      { key: "add_vendors", label: "Add Vendors" },
      { key: "edit_vendors", label: "Edit Vendors" },
      { key: "delete_vendors", label: "Delete Vendors" },
    ]
  },
  {
    name: "Purchase Orders",
    group: "purchase",
    icon: FileSpreadsheet,
    permissions: [
      { key: "view_purchase_orders", label: "View Purchase Orders" },
      { key: "add_purchase_orders", label: "Add Purchase Orders" },
      { key: "edit_purchase_orders", label: "Edit Purchase Orders" },
      { key: "delete_purchase_orders", label: "Delete Purchase Orders" },
    ]
  },
  {
    name: "Purchase Challans (GRN)",
    group: "purchase",
    icon: FileSpreadsheet,
    permissions: [
      { key: "view_purchase_challans", label: "View Purchase Challans" },
      { key: "add_purchase_challans", label: "Add Purchase Challans" },
      { key: "edit_purchase_challans", label: "Edit Purchase Challans" },
      { key: "delete_purchase_challans", label: "Delete Purchase Challans" },
    ]
  },
  {
    name: "Purchase Invoices",
    group: "purchase",
    icon: Receipt,
    permissions: [
      { key: "view_purchase_invoices", label: "View Purchase Invoices" },
      { key: "add_purchase_invoices", label: "Add Purchase Invoices" },
      { key: "edit_purchase_invoices", label: "Edit Purchase Invoices" },
      { key: "delete_purchase_invoices", label: "Delete Purchase Invoices" },
    ]
  },
  {
    name: "Vendor Ledger",
    group: "purchase",
    icon: BookOpen,
    permissions: [
      { key: "view_vendor_ledger", label: "View Vendor Ledger" },
    ]
  },
  {
    name: "Chart of Accounts",
    group: "accounting",
    icon: BookOpen,
    permissions: [
      { key: "view_chart_of_accounts", label: "View Chart of Accounts" },
      { key: "add_accounts", label: "Add Accounts" },
      { key: "edit_accounts", label: "Edit Accounts" },
      { key: "delete_accounts", label: "Delete Accounts" },
    ]
  },
  {
    name: "Ledgers",
    group: "accounting",
    icon: BookOpen,
    permissions: [
      { key: "view_general_ledger", label: "View General Ledger" },
      { key: "add_ledger", label: "Create Ledger" },
    ]
  },
  {
    name: "Bank Accounts",
    group: "accounting",
    icon: Building2,
    permissions: [
      { key: "view_bank_accounts", label: "View Bank Accounts" },
      { key: "add_bank_accounts", label: "Add Bank Accounts" },
      { key: "edit_bank_accounts", label: "Edit Bank Accounts" },
      { key: "delete_bank_accounts", label: "Delete Bank Accounts" },
    ]
  },
  {
    name: "Receipts & Payments",
    group: "accounting",
    icon: CreditCard,
    permissions: [
      { key: "view_receipts", label: "View Receipts" },
      { key: "add_receipts", label: "Add Receipts" },
      { key: "edit_receipts", label: "Edit Receipts" },
      { key: "delete_receipts", label: "Delete Receipts" },
      { key: "view_payments", label: "View Payments" },
      { key: "add_payments", label: "Add Payments" },
      { key: "edit_payments", label: "Edit Payments" },
      { key: "delete_payments", label: "Delete Payments" },
    ]
  },
  {
    name: "Journal Entries",
    group: "accounting",
    icon: Wallet,
    permissions: [
      { key: "view_journal_entries", label: "View Journal Entries" },
      { key: "add_journal_entries", label: "Add Journal Entries" },
      { key: "edit_journal_entries", label: "Edit Journal Entries" },
      { key: "delete_journal_entries", label: "Delete Journal Entries" },
    ]
  },
  {
    name: "Sales Invoices",
    group: "accounting",
    icon: Receipt,
    permissions: [
      { key: "view_sales_invoices", label: "View Sales Invoices" },
      { key: "add_sales_invoices", label: "Add Sales Invoices" },
      { key: "edit_sales_invoices", label: "Edit Sales Invoices" },
      { key: "delete_sales_invoices", label: "Delete Sales Invoices" },
      { key: "write_accounting", label: "Write Access to Accounting" },
    ]
  },
  {
    name: "Payment Receipts",
    group: "accounting",
    icon: CreditCard,
    permissions: [
      { key: "view_payment_receipts", label: "View Payment Receipts" },
      { key: "add_payment_receipts", label: "Add Payment Receipts" },
      { key: "edit_payment_receipts", label: "Edit Payment Receipts" },
    ]
  },
  {
    name: "Day Book",
    group: "accounting",
    icon: BookOpen,
    permissions: [
      { key: "view_day_book", label: "View Day Book" },
    ]
  },
  {
    name: "Trial Balance",
    group: "accounting",
    icon: BookOpen,
    permissions: [
      { key: "view_trial_balance", label: "View Trial Balance" },
    ]
  },
  {
    name: "Customer Ledger",
    group: "accounting",
    icon: Users,
    permissions: [
      { key: "view_customer_ledger", label: "View Customer Ledger" },
    ]
  },
  {
    name: "Profit & Loss",
    group: "accounting",
    icon: BarChart3,
    permissions: [
      { key: "view_profit_loss", label: "View Profit & Loss" },
    ]
  },
  {
    name: "Balance Sheet",
    group: "accounting",
    icon: FileSpreadsheet,
    permissions: [
      { key: "view_balance_sheet", label: "View Balance Sheet" },
    ]
  },
  {
    name: "Bills Receivable/Payable",
    group: "accounting",
    icon: Receipt,
    permissions: [
      { key: "view_outstanding", label: "View Bills Receivable/Payable" },
    ]
  },
  {
    name: "Cost Centres",
    group: "accounting",
    icon: BarChart3,
    permissions: [
      { key: "view_cost_centres", label: "View Cost Centre Summary" },
    ]
  },
  {
    name: "Bank Reconciliation",
    group: "accounting",
    icon: Building2,
    permissions: [
      { key: "view_bank_reconciliation", label: "View Bank Reconciliation" },
    ]
  },
  {
    name: "Reports",
    group: "reports",
    icon: BarChart3,
    permissions: [
      { key: "view_reports", label: "View Reports" },
      { key: "view_report_master", label: "Master Report" },
      { key: "view_report_heats", label: "Heat Report" },
      { key: "view_report_sales", label: "Sales Analysis" },
      { key: "view_report_costing", label: "Costing" },
      { key: "export_reports", label: "Export Reports" },
      { key: "edit_costing", label: "Edit Costing Inputs" },
    ]
  },
  {
    name: "System Settings",
    group: "system",
    icon: Settings,
    permissions: [
      { key: "view_business_profile", label: "View Business Profile" },
      { key: "edit_business_profile", label: "Edit Business Profile" },
    ]
  },
  {
    name: "User Management",
    group: "system",
    icon: Shield,
    permissions: [
      { key: "view_users", label: "View Users" },
      { key: "add_users", label: "Add Users" },
      { key: "edit_users", label: "Edit Users" },
      { key: "delete_users", label: "Delete Users" },
      { key: "block_users", label: "Block/Unblock Users" },
      { key: "hold_users", label: "Hold/Activate Users" },
      { key: "manage_permissions", label: "Manage User Permissions" },
      { key: "view_user_devices", label: "View User Devices" },
      { key: "revoke_user_devices", label: "Revoke User Devices" },
    ]
  },
  {
    name: "Data Management",
    group: "system",
    icon: Database,
    permissions: [
      { key: "import_data", label: "Import Data" },
      { key: "export_data", label: "Export Data" },
    ]
  },
  {
    name: "Sub Contract / Machining",
    group: "subcontract",
    icon: Settings,
    permissions: [
      { key: "view_machining", label: "View Machining Orders" },
      { key: "add_machining", label: "Add Machining Orders" },
      { key: "edit_machining", label: "Edit Machining Orders" },
      { key: "delete_machining", label: "Delete Machining Orders" },
    ]
  },
  {
    name: "Chemistry Elements",
    group: "masters",
    icon: Beaker,
    permissions: [
      { key: "view_chemistry_elements", label: "View Chemistry Elements" },
      { key: "add_chemistry_elements", label: "Add Chemistry Elements" },
      { key: "edit_chemistry_elements", label: "Edit Chemistry Elements" },
      { key: "delete_chemistry_elements", label: "Delete Chemistry Elements" },
    ]
  },
  {
    name: "Tally Integration",
    group: "accounting",
    icon: Database,
    permissions: [
      { key: "view_tally_co", label: "View Tally Integration" },
      { key: "sync_tally_co", label: "Sync Tally Data" },
      { key: "edit_tally_co", label: "Edit Tally Mappings" },
      { key: "view_tally_bridge", label: "View Tally Bridge" },
      { key: "edit_tally_bridge", label: "Edit Tally Bridge Mappings" },
    ]
  },
  {
    name: "Print Templates",
    group: "system",
    icon: Database,
    permissions: [
      { key: "view_print_templates", label: "View Print Templates" },
      { key: "add_print_templates", label: "Add Print Templates" },
      { key: "edit_print_templates", label: "Edit Print Templates" },
      { key: "delete_print_templates", label: "Delete Print Templates" },
    ]
  },
  {
    name: "Approvals",
    group: "system",
    icon: Shield,
    restrictedToAdmins: true, // Only Owner, Admin, Developer can see/manage this
    permissions: [
      { key: "view_approvals", label: "View Approvals" },
      { key: "manage_approvals", label: "Manage Approval Settings" },
      { key: "approve_requests", label: "Approve/Reject Requests" },
    ]
  },
  {
    name: "Notifications",
    group: "system",
    icon: Bell,
    restrictedToAdmins: true, // Only Owner, Admin, Developer can see/manage this
    permissions: [
      { key: "send_notifications", label: "Send Notifications" },
      { key: "send_announcement", label: "Send Announcements" },
      { key: "view_notification_history", label: "View Notification History" },
    ]
  },
  {
    name: "Developer Tools",
    group: "system",
    icon: Settings,
    restrictedToAdmins: true, // Only Owner, Admin, Developer can see/manage this
    permissions: [
      { key: "developer_access", label: "Developer Dashboard Access" },
    ]
  },
  {
    name: "Dashboards",
    group: "system",
    icon: LayoutDashboard,
    restrictedToAdmins: true, // Only Owner, Admin, Developer can see/manage this
    permissions: [
      { key: "manage_dashboards", label: "Manage Custom Dashboards" },
    ]
  },
  {
    name: "Activity Log",
    group: "system",
    icon: Activity,
    restrictedToAdmins: true, // Only Owner, Admin, Developer can see/manage this
    permissions: [
      { key: "view_activity_log", label: "View Activity Log" },
    ]
  },
  {
    name: "AI Assistant",
    group: "system",
    icon: Activity, // Using Activity as placeholder - can be changed to Sparkles
    restrictedToAdmins: true, // Only Owner, Admin, Developer can see/manage this
    permissions: [
      { key: "use_ai_assistant", label: "Use AI Assistant" },
    ]
  },
  {
    name: "Special Pages",
    group: "system",
    icon: Store,
    restrictedToAdmins: true, // Only Owner, Admin, Developer can grant these
    permissions: [
      { key: "access_bnb", label: "B&B — Open & Read (Bath & Beauty)" },
      { key: "edit_bnb", label: "B&B — Edit Data & Sheets" },
      { key: "access_banamob", label: "BANAMOB — Open & Read" },
      { key: "edit_banamob", label: "BANAMOB — Edit Data" },
    ]
  },
];

// Navigation permission mapping
export const NAV_PERMISSIONS = {
  "/": "view_dashboard",
  "/customers": "view_customers",
  "/marketing": "view_marketing",
  "/castings": "view_castings",
  "/patterns": "view_patterns",
  "/grades": "view_grades",
  "/materials": "view_materials",
  "/system/furnaces": "view_furnaces",
  "/monthly-planning": "view_monthly_plan",
  "/daily-planning": "view_daily_plan",
  "/heats": "view_heats",
  "/fettling": "view_fettling",
  "/sub-connect/pattern-num": "view_pattern_num",
  "/sub-connect/ready-cheques": "view_ready_cheques",
  "/sub-connect/msedcl-readings": "view_msedcl_readings",
  "/sub-connect/sand-testing": "view_sand_testing_reports",
  "/sub-connect/closing-stock": "view_closing_stock",
  "/quality": "view_quality",
  "/exce-reports": "view_exce_reports",
  "/dispatch": "view_dispatch",
  "/sales": "view_sales",
  "/sales/pending-orders": "view_sales",
  "/sales-dashboard": "view_sales",
  "/status": "view_status",
  "/status/sales-orders": "view_status",
  "/status/pattern-orders": "view_status",
  "/status/machining-orders": "view_status",
  "/stock": "view_stock",
  "/store": "view_store",
  "/store/consumption": "view_store_consumption",
  "/store/history": "view_store_history",
  "/purchase/vendors": "view_vendors",
  "/purchase/orders": "view_purchase_orders",
  "/purchase/challans": "view_purchase_challans",
  "/purchase/invoices": "view_purchase_invoices",
  "/accounting/day-book": "view_day_book",
  "/accounting/bank-accounts": "view_bank_accounts",
  "/accounting/balance-sheet": "view_balance_sheet",
  "/accounting/balance-sheet/profit-and-loss": "view_balance_sheet",
  "/accounting/balance-sheet/trial-balance": "view_balance_sheet",
  "/accounting/chart-of-accounts": "view_chart_of_accounts",
  "/accounting/chart-of-accounts/groups": "view_chart_of_accounts",
  "/accounting/chart-of-accounts/ledgers": "view_chart_of_accounts",
  "/accounting/chart-of-accounts/voucher-types": "view_chart_of_accounts",
  "/accounting/bills-payable": "view_outstanding",
  "/accounting/bills-receivable": "view_outstanding",
  "/accounting/advances": "view_outstanding",
  "/accounting/sales-invoices": "view_sales_invoices",
  "/accounting/sales-data": "view_sales_invoices",
  "/purchase/data": "view_purchase_invoices",
  "/tally-co": "view_tally_co",
  "/machining": "view_machining",
  "/machining-vendors": "view_machining",
  "/pattern-vendors": "view_machining",
  "/pattern-contracts": "view_machining",
  "/sub-contract/machining-contracts": "view_machining",
  "/sub-contract/machining-vendors": "view_machining",
  "/sub-contract/pattern-vendors": "view_machining",
  "/sub-contract/pattern-contracts": "view_machining",
  "/sub-contract/contract-invoices": "view_sales_invoices",
  "/chemistry-elements": "view_chemistry_elements",
  "/purchase/vendor-ledger": "view_vendor_ledger",
  "/reports": ["view_report_master", "view_report_costing", "view_report_heats", "view_report_sales"],
  "/reports/master": "view_report_master",
  "/reports/costing": "view_report_costing",
  "/reports/heats": "view_report_heats",
  "/reports/sales": "view_report_sales",
  "/system/business-profile": "view_business_profile",
  "/system/users": "view_users",
  "/import": "import_data",
  "/export": "export_data",
  "/system/get-data": "export_data",
  "/print-canvas": "view_print_templates",
  "/system/approvals": "view_approvals",
  "/system/approvals/requests": "view_approvals",
  "/system/approvals/settings": "manage_approvals",
  "/system/approvals/credentials": "view_approvals",
  "/system/approvals/status": "developer_access",
  "/system/notifications": "send_notifications",
  "/system/developer": "developer_access",
  "/system/developer-tools": "developer_access",
  "/system/data": "developer_access",
  "/system/duplicates": "developer_access",
  "/system/maintenance": "developer_access",
  "/system/dashboards": "manage_dashboards",
  "/system/activity": "view_activity_log",
  "/system/activity/users": "view_activity_log",
  "/system/terms-conditions": "view_business_profile",
  "/system/furnaces": "view_furnaces",
  "/system/ai-settings": "use_ai_assistant",
  "/system/llm": "use_ai_assistant",
  "/system/status": "developer_access",
  "/print-preview": "view_print_templates",
  "/print-canvas/canvas": "view_print_templates",
  "/print-canvas/upload": "view_print_templates",
  "/store/requests": "view_store",
  "/bnb": "access_bnb",
  "/banamob": "access_banamob",
};

export const PermissionsProvider = ({ children }) => {
  const { user } = useAuth();

  // Get user permissions from metadata
  const userPermissions = user?.permissions || {};

  // Check if user has any permissions set at all. With no map configured we fall
  // back to the defaults for the user's role rather than to full access — see
  // backend/utils/permissions.js, which must stay in step with this.
  const hasNoPermissionsConfigured = Object.keys(userPermissions).length === 0;

  // Roles trusted with write access when they carry no explicit permissions map.
  const DEFAULT_WRITE_ROLES = ["owner", "developer", "admin"];

  // Check if user is an Owner (case-insensitive)
  const isOwner = user?.role?.toLowerCase() === "owner";

  // Check if user is a Developer (case-insensitive) - has same access as Owner
  const isDeveloper = user?.role?.toLowerCase() === "developer";

  // Check if user has premium access (bypass all permissions)
  const isPremium = user?.is_premium === true;

  // Check if user has full access (Owner, Developer, or Premium)
  const hasFullAccess = isOwner || isDeveloper || isPremium;

  // Check if user is on hold (temporarily suspended)
  const isOnHold = user?.is_on_hold === true;

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    // Users on hold have no permissions (except owners/developers)
    if (isOnHold && !hasFullAccess) return false;
    // If no permission is required, allow access
    if (!permission) return true;
    // Owners, Developers and Premium users always have full access
    if (hasFullAccess) return true;
    // With no permissions map, fall back to the role's defaults: trusted roles
    // keep write access, everyone else may read but not change anything.
    if (hasNoPermissionsConfigured) {
      if (DEFAULT_WRITE_ROLES.includes(user?.role?.toLowerCase())) return true;
      if (permission.startsWith("view_report_")) return false;
      return permission.startsWith("view_");
    }
    // Otherwise, check the specific permission
    return userPermissions[permission] === true;
  };

  // Check if user has any of the specified permissions
  const hasAnyPermission = (permissions) => {
    if (!permissions || permissions.length === 0) return true;
    return permissions.some(p => hasPermission(p));
  };

  // Check if user has all of the specified permissions
  const hasAllPermissions = (permissions) => {
    if (!permissions || permissions.length === 0) return true;
    return permissions.every(p => hasPermission(p));
  };

  // Check if user can access a specific route
  const canAccessRoute = (path) => {
    // Per-user profile pages (/system/activity/users/:id) inherit the log's gate.
    const permission = NAV_PERMISSIONS[path]
      ?? (path.startsWith("/system/activity/") ? NAV_PERMISSIONS["/system/activity"] : undefined);
    if (!permission) return true; // No permission required
    return Array.isArray(permission) ? permission.some(hasPermission) : hasPermission(permission);
  };

  return (
    <PermissionsContext.Provider value={{
      permissions: userPermissions,
      userRole: user?.role,
      isOwner,
      isDeveloper,
      isPremium,
      hasFullAccess,
      isOnHold,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      canAccessRoute,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within a PermissionsProvider");
  }
  return context;
};

export default PermissionsContext;
